/**
 * PMGT FE-2 — shared building blocks for the unified `ProjectWizard`.
 *
 * Extracted VERBATIM from the original `CreateProjectPage.tsx` so the create
 * flow stays byte-for-byte identical while the same primitives are reused by
 * the edit flow:
 *   - party / reporting value types + their field editors;
 *   - draft park / draft turbine row types + the `ParkRow` / `TurbineRow`
 *     editors (find-or-create typeaheads, FB-04/05/06);
 *   - the holistic validator (FB-08) + the step-error helpers;
 *   - the Basics step fields (name / dates / offer ref / report language).
 *
 * Both modes share these; the mode-specific orchestration (mint-on-submit for
 * create vs. live PATCH/add/remove for edit) lives in `ProjectWizard.tsx` and
 * `EditParksStep.tsx`.
 */
import { useEffect, useMemo, useState, type ReactElement } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  Link,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import type { KnownTenant } from "../../tenancy/types.js";
import { useSitesSearchQuery } from "../../sites/queries/hooks.js";
import type { Site } from "../../sites/queries/types.js";
import {
  useAssetModelsForTypeQuery,
  useSiteTurbinesQuery,
  useTurbinesSearchQuery,
  type Turbine,
} from "../../turbines/queries/index.js";
import { CountryAutocomplete } from "../../sites/shared/CountryAutocomplete.js";
import { AssetTypeAutocomplete } from "../../turbines/shared/AssetTypeAutocomplete.js";
import { AssetModelAutocomplete } from "../../turbines/shared/AssetModelAutocomplete.js";
import { AddTurbineModelDialog } from "../../turbine-models/modal-add/AddTurbineModelDialog.js";
import { ContactPicker } from "../../contacts/ContactPicker.js";
import { InfoIcon, PlusIcon, Trash2Icon } from "@aeriosiq/ui/icons";
import {
  SET_BY_WORKFLOW,
  TODAY_RULES,
  isHidden,
  isPrefilled,
  isRequired,
  partyRules,
  type FieldRules,
} from "./fieldRules.js";

/* ── per-party info copy + headline (FB-20) ──────────────────────────── */

/**
 * FB-20 — the per-party "where is this visible + how does it share" copy,
 * sourced from the role table in `.vibe/_CROSS_TENANT/05_COMPANY_VISIBILITY.md`
 * (XT-D20..D23). Each line names the surface the role sees and the sharing
 * effect: naming a partner *tenant* auto-grants the role-scoped access
 * (XT-D23); naming a contact never shares. Kept to 1–2 sentences.
 */
export const PARTY_INFO = {
  projectCustomer:
    "Sees the project, its state, and published reports only — no editing (outcome, not workshop). Naming a partner company shares the project with them automatically; a contact never shares.",
  projectContractor:
    "Views the project, contracts and positions, can create/edit inspection instructions, evaluate data acquisitions, and view final reports. Naming a partner company shares the project with them automatically; a contact never shares.",
  contractCustomer:
    "Contract-scoped: sees the project shell (stats filtered to their contract) and that contract's reports only. Naming a partner company shares that contract with them automatically.",
  contractContractor:
    "Contract-scoped: sees that contract with its positions, inspection instructions, data acquisitions and reports. Naming a partner company shares that contract with them automatically.",
  reportingSubcontractor:
    "Project-wide: views the project and inspection instructions (read-only), evaluates data acquisitions, and creates/edits reports; no contract access. Must be a partner company — naming one shares the project with them automatically.",
} as const;

/** FB-20 — a small info affordance (`?`/InfoOutlined in a Tooltip), reused on
 *  every party headline. The `aria-label` carries the party name so a11y +
 *  tests can target it by accessible name. */
export function PartyInfoIcon({
  label,
  text,
}: {
  /** Accessible name, e.g. "About the Project Customer party". */
  label: string;
  text: string;
}): ReactElement {
  return (
    <Tooltip title={text}>
      <IconButton size="small" aria-label={label} sx={{ p: 0.25 }}>
        <InfoIcon fontSize="small" color="action" />
      </IconButton>
    </Tooltip>
  );
}

/** FB-20 — a party section headline: title (+ optional required star) with the
 *  per-party info icon to its right. Shared by every party field so all five
 *  headlines look + behave identically. */
export function PartyHeadline({
  title,
  required = false,
  error = false,
  infoLabel,
  infoText,
}: {
  title: string;
  required?: boolean;
  error?: boolean;
  infoLabel: string;
  infoText: string;
}): ReactElement {
  return (
    <Stack direction="row" spacing={0.5} alignItems="center">
      <Typography
        variant="subtitle2"
        {...(error ? { color: "error" as const } : {})}
      >
        {title}
        {required ? " *" : ""}
      </Typography>
      <PartyInfoIcon label={infoLabel} text={infoText} />
    </Stack>
  );
}

/* ── M3C-04 — the Basics fields a workflow rule can address ─────────── */

export const BASICS_FIELDS = [
  "offerReference",
  "reportLanguage",
  "startDate",
  "endDate",
  "aiModelIds",
] as const;
export type BasicsField = (typeof BASICS_FIELDS)[number];
export const BASICS_LABELS: Record<BasicsField, string> = {
  offerReference: "Offer reference",
  reportLanguage: "Report language",
  startDate: "Start date",
  endDate: "End date",
  aiModelIds: "AI models",
};

/** M3C-04 — a party the workflow fixes: read-only, "set by the workflow". */
export function PrefilledPartyField({
  title,
  value,
  knownTenants,
  testId,
}: {
  title: string;
  value: string | null;
  knownTenants: readonly KnownTenant[];
  testId: string;
}): ReactElement {
  const name =
    value === null
      ? null
      : (knownTenants.find((t) => t.tenantId === value)?.tenantName ?? value);
  return (
    <Box
      component="section"
      aria-label={`${title} party`}
      data-testid={testId}
      sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}
    >
      <Typography variant="subtitle2">{title}</Typography>
      <TextField
        size="small"
        label={title}
        value={name ?? "The workflow names this party"}
        helperText={SET_BY_WORKFLOW}
        slotProps={{ htmlInput: { readOnly: true, "aria-readonly": true } }}
        sx={{ maxWidth: 360 }}
      />
    </Box>
  );
}

/* ── party (create-mode) ─────────────────────────────────────────────── */

/** Customer / contractor only — the reporting subcontractor has its own
 *  tenant-only two-option control (`ReportingPartyField`, FB-03). */
export type PartyKind = "customer" | "contractor";
export type PartyMode = "tenant" | "contact";

/** Create-form party value: either a known tenant (auto-grants on the BE,
 *  XT-D23) or a free contact (descriptive only, never grants — PM-D23).
 *  CM-9 — `contactId` carries the address-book contact id chosen in
 *  "Address only (contact)" mode; it maps to `customerContactId` /
 *  `contractorContactId` on submit and NEVER auto-grants. */
export interface PartyValue {
  mode: PartyMode;
  tenantId: string | null;
  contactId: string | null;
}

export const EMPTY_PARTY: PartyValue = {
  mode: "tenant",
  tenantId: null,
  contactId: null,
};

/** FB-43 — resolve a project `PartyValue` from prefilled ids (an existing
 *  project's `*_tenant_id` / `*_contact_id`). A contact id wins the mode (the
 *  party was set "address only"); otherwise tenant mode (even when both ids are
 *  null). Used to seed the edit project-party pickers from `ProjectDetail`. */
export function partyValueFromIds(
  tenantId: string | null | undefined,
  contactId: string | null | undefined,
): PartyValue {
  if (contactId != null && contactId.length > 0) {
    return { mode: "contact", tenantId: null, contactId };
  }
  return {
    mode: "tenant",
    tenantId: tenantId != null && tenantId.length > 0 ? tenantId : null,
    contactId: null,
  };
}

const PARTY_TITLE: Record<PartyKind, string> = {
  customer: "Customer",
  contractor: "Contractor",
};

/** FB-20 — the headline shown above the field. "Project Customer" /
 *  "Project Contractor" disambiguates them from the contract-scoped parties.
 *  Kept separate from `PARTY_TITLE` because the combobox/aria labels
 *  ("Customer — partner company") are load-bearing for existing tests. */
const PARTY_HEADLINE: Record<PartyKind, string> = {
  customer: "Project Customer",
  contractor: "Project Contractor",
};

const PARTY_INFO_BY_KIND: Record<PartyKind, string> = {
  customer: PARTY_INFO.projectCustomer,
  contractor: PARTY_INFO.projectContractor,
};

/** Customer + contractor are required (legacy `Project` needs name + customer
 *  + contractor — FB-12). The star + enforcement live here. */
const PARTY_REQUIRED: Record<PartyKind, boolean> = {
  customer: true,
  contractor: true,
};

const PARTY_HINT: Record<PartyKind, string> = {
  customer:
    "The project customer. Naming a partner company shares the project with them.",
  contractor:
    "The contractor running the job. Naming a partner company shares the project with them.",
};

/**
 * Create-mode party field. Reuses the same active-known-tenant set as the
 * shipped `PartyPicker` (`useKnownTenantsQuery`) but, instead of firing an
 * access-grant against an existing project, it lifts the selected tenant id
 * into the create form so it can ride along in the `POST /projects` body
 * (PM-D21) — the BE auto-grants. "Address only" is the descriptive,
 * non-sharing choice (a contact); v1 has no contact directory wired, so the
 * contact branch is intentionally inert here (mirrors PartyPicker).
 */
export function CreatePartyField({
  kind,
  value,
  onChange,
  knownTenants,
  knownTenantsLoading,
  error,
  modes = ["tenant", "contact"],
  required: requiredOverride,
}: {
  kind: PartyKind;
  value: PartyValue;
  onChange: (next: PartyValue) => void;
  knownTenants: readonly KnownTenant[];
  knownTenantsLoading: boolean;
  error: string | null;
  /** M3C-04 — the modes the workflow leaves (a hidden `*ContactId` = tenant only). */
  modes?: readonly PartyMode[];
  /** M3C-04 — the workflow rule, required or optional (absent = as before). */
  required?: boolean;
}): ReactElement {
  const options = useMemo(
    () =>
      [...knownTenants].sort((a, b) =>
        a.tenantName.localeCompare(b.tenantName),
      ),
    [knownTenants],
  );
  const noKnownTenants = !knownTenantsLoading && options.length === 0;
  const required = requiredOverride ?? PARTY_REQUIRED[kind];
  const onlyMode = modes.length === 1 ? modes[0] : undefined;
  // a mode the workflow hides is never the one shown
  const mode: PartyMode = onlyMode ?? value.mode;

  return (
    <Box
      component="section"
      aria-label={`${PARTY_TITLE[kind]} party`}
      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
    >
      <PartyHeadline
        title={PARTY_HEADLINE[kind]}
        required={required}
        error={error !== null}
        infoLabel={`About the ${PARTY_HEADLINE[kind]} party`}
        infoText={PARTY_INFO_BY_KIND[kind]}
      />
      <Typography variant="caption" color="text.secondary">
        {PARTY_HINT[kind]}
      </Typography>

      {onlyMode === undefined ? (
        <RadioGroup
          row
          value={value.mode}
          onChange={(_e, v) => {
            onChange({ mode: v as PartyMode, tenantId: null, contactId: null });
          }}
          aria-label={`${PARTY_TITLE[kind]} sharing mode`}
        >
          <FormControlLabel
            value="tenant"
            control={<Radio size="small" />}
            label="Share with a partner company"
          />
          <FormControlLabel
            value="contact"
            control={<Radio size="small" />}
            label="Address only (no sharing)"
          />
        </RadioGroup>
      ) : null}

      {mode === "contact" ? (
        <Box sx={{ maxWidth: 480 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            A contact is an address for reports / invoices only — it does not
            share the project (PM-D23).
          </Typography>
          {/* CM-9 — the real ContactPicker (search active contacts + inline
              add-new). The chosen contactId rides along as the party's
              `*_contact_id`; descriptive only, never grants (CM-D13). */}
          <ContactPicker
            value={value.contactId}
            onChange={(contactId) => {
              onChange({ mode: "contact", tenantId: null, contactId });
            }}
            label={`${PARTY_TITLE[kind]} contact`}
            error={error}
          />
        </Box>
      ) : noKnownTenants ? (
        <Alert severity="warning" variant="outlined">
          You aren&apos;t linked to any partner companies yet. Invite them
          first, then you can share this project.{" "}
          <Link
            component={RouterLink}
            to="/admin/partner-companies"
            underline="always"
          >
            Go to Partner Companies
          </Link>
        </Alert>
      ) : (
        <TextField
          select
          size="small"
          label={`${PARTY_TITLE[kind]} — partner company`}
          required={required}
          error={error !== null}
          helperText={error ?? undefined}
          value={value.tenantId ?? ""}
          onChange={(e) => {
            const v = e.target.value;
            onChange({
              mode: "tenant",
              tenantId: v.length > 0 ? v : null,
              contactId: null,
            });
          }}
          sx={{ maxWidth: 360 }}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {options.map((t) => (
            <MenuItem key={t.tenantId} value={t.tenantId}>
              {t.tenantName}
            </MenuItem>
          ))}
        </TextField>
      )}
    </Box>
  );
}

/* ── party (edit-mode, PEDIT-PARTIES) ────────────────────────────────── */

/** A `{tenantId, tenantName}` pair the edit pickers can show even when the
 *  tenant is NOT (or no longer) in the caller's known-tenants set — the case
 *  for MIGRATED projects whose party tenant resolves to a real platform tenant
 *  but isn't a current active link. Names come from the `ProjectDetail` /
 *  `ProjectListItem` / `ContractListItem` party-name fields the BE resolves. */
export interface PrefilledTenant {
  tenantId: string;
  tenantName: string | null;
}

/** Merge a prefilled party tenant into the known-tenants option set so the
 *  select can render its NAME even when it isn't a current known link. Sorted
 *  by name; de-duped by id. The synthetic option uses the resolved name (or the
 *  id as a last resort) so a migrated party never renders blank. */
function optionsWithPrefilled(
  knownTenants: readonly KnownTenant[],
  prefilled: PrefilledTenant | null,
): { tenantId: string; tenantName: string }[] {
  const byId = new Map<string, string>();
  for (const t of knownTenants) byId.set(t.tenantId, t.tenantName);
  if (
    prefilled !== null &&
    prefilled.tenantId.length > 0 &&
    !byId.has(prefilled.tenantId)
  ) {
    byId.set(prefilled.tenantId, prefilled.tenantName ?? prefilled.tenantId);
  }
  return [...byId.entries()]
    .map(([tenantId, tenantName]) => ({ tenantId, tenantName }))
    .sort((a, b) => a.tenantName.localeCompare(b.tenantName));
}

/**
 * Edit-mode project party field (Customer / Contractor). FB-43 — now a
 * tenant|contact toggle (full parity with create's `CreatePartyField`), so a
 * party can be SWITCHED tenant↔contact (the key use case: a contact later joins
 * as a tenant). Prefilled from the live project's `*_tenant_id` / `*_contact_id`
 * (the mode follows whichever is set; a contact id wins). Tenant mode → the
 * prefillable known-tenant select (naming a tenant rides along in the
 * `PATCH /projects/{id}` party body, XT-D23 auto-grant; the current party tenant
 * is always an option so a migrated party shows its NAME). Contact mode → the
 * `ContactPicker` (descriptive only, never grants). "None" / no contact clears
 * the party (soft-revokes the auto grant).
 */
export function EditPartyField({
  kind,
  value,
  prefilled,
  onChange,
  knownTenants,
  knownTenantsLoading,
}: {
  kind: PartyKind;
  /** The current party value (tenant or contact). */
  value: PartyValue;
  /** The live party tenant + resolved name, so it shows even when unknown. */
  prefilled: PrefilledTenant | null;
  onChange: (next: PartyValue) => void;
  knownTenants: readonly KnownTenant[];
  knownTenantsLoading: boolean;
}): ReactElement {
  const options = useMemo(
    () => optionsWithPrefilled(knownTenants, prefilled),
    [knownTenants, prefilled],
  );
  const noOptions = !knownTenantsLoading && options.length === 0;

  return (
    <Box
      component="section"
      aria-label={`${PARTY_TITLE[kind]} party`}
      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
    >
      <PartyHeadline
        title={PARTY_HEADLINE[kind]}
        infoLabel={`About the ${PARTY_HEADLINE[kind]} party`}
        infoText={PARTY_INFO_BY_KIND[kind]}
      />
      <Typography variant="caption" color="text.secondary">
        {PARTY_HINT[kind]}
      </Typography>

      <RadioGroup
        row
        value={value.mode}
        onChange={(_e, v) => {
          onChange({ mode: v as PartyMode, tenantId: null, contactId: null });
        }}
        aria-label={`${PARTY_TITLE[kind]} sharing mode`}
      >
        <FormControlLabel
          value="tenant"
          control={<Radio size="small" />}
          label="Share with a partner company"
        />
        <FormControlLabel
          value="contact"
          control={<Radio size="small" />}
          label="Address only (no sharing)"
        />
      </RadioGroup>

      {value.mode === "contact" ? (
        <Box sx={{ maxWidth: 480 }}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 1 }}
          >
            A contact is an address for reports / invoices only — it does not
            share the project (PM-D23).
          </Typography>
          <ContactPicker
            value={value.contactId}
            onChange={(contactId) => {
              onChange({ mode: "contact", tenantId: null, contactId });
            }}
            label={`${PARTY_TITLE[kind]} contact`}
          />
        </Box>
      ) : noOptions ? (
        <Alert severity="warning" variant="outlined">
          You aren&apos;t linked to any partner companies yet. Invite them
          first, then you can share this project.{" "}
          <Link
            component={RouterLink}
            to="/admin/partner-companies"
            underline="always"
          >
            Go to Partner Companies
          </Link>
        </Alert>
      ) : (
        <TextField
          select
          size="small"
          label={`${PARTY_TITLE[kind]} — partner company`}
          value={value.tenantId ?? ""}
          onChange={(e) => {
            const v = e.target.value;
            onChange({
              mode: "tenant",
              tenantId: v.length > 0 ? v : null,
              contactId: null,
            });
          }}
          sx={{ maxWidth: 360 }}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {options.map((t) => (
            <MenuItem key={t.tenantId} value={t.tenantId}>
              {t.tenantName}
            </MenuItem>
          ))}
        </TextField>
      )}
    </Box>
  );
}

/**
 * Edit-mode reporting-subcontractor field (PEDIT-PARTIES). Tenant-only, like
 * `ReportingPartyField` — but a plain prefillable select rather than the
 * our-team/partner radio, because in edit mode the live value is simply
 * "the reporting tenant, or none". Naming a tenant auto-grants
 * `reporting_subcontractor`; "None" clears + soft-revokes.
 */
export function EditReportingPartyField({
  value,
  prefilled,
  onChange,
  knownTenants,
  knownTenantsLoading,
}: {
  value: string | null;
  prefilled: PrefilledTenant | null;
  onChange: (tenantId: string | null) => void;
  knownTenants: readonly KnownTenant[];
  knownTenantsLoading: boolean;
}): ReactElement {
  const options = useMemo(
    () => optionsWithPrefilled(knownTenants, prefilled),
    [knownTenants, prefilled],
  );
  const noOptions = !knownTenantsLoading && options.length === 0;

  return (
    <Box
      component="section"
      aria-label="Reporting subcontractor party"
      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
    >
      <PartyHeadline
        title="Reporting Subcontractor"
        infoLabel="About the Reporting Subcontractor party"
        infoText={PARTY_INFO.reportingSubcontractor}
      />
      <Typography variant="caption" color="text.secondary">
        Who will do the reporting? Choose a partner company, or leave as our
        team (None).
      </Typography>

      {noOptions ? (
        <Alert severity="info" variant="outlined" sx={{ maxWidth: 360 }}>
          No partner companies to hand the reporting to yet.
        </Alert>
      ) : (
        <TextField
          select
          size="small"
          label="Reporting partner company"
          value={value ?? ""}
          onChange={(e) => {
            const v = e.target.value;
            onChange(v.length > 0 ? v : null);
          }}
          sx={{ maxWidth: 360 }}
        >
          <MenuItem value="">
            <em>Our team (None)</em>
          </MenuItem>
          {options.map((t) => (
            <MenuItem key={t.tenantId} value={t.tenantId}>
              {t.tenantName}
            </MenuItem>
          ))}
        </TextField>
      )}
    </Box>
  );
}

/* ── reporting subcontractor (FB-03 / PM-D30) ────────────────────────── */

export type ReportingChoice = "our_team" | "partner";
export interface ReportingValue {
  choice: ReportingChoice;
  /** Set only when `choice === "partner"`. */
  tenantId: string | null;
}

export const EMPTY_REPORTING: ReportingValue = {
  choice: "our_team",
  tenantId: null,
};

/** A reporting value is satisfied (valid) when EITHER our team does the
 *  reporting, OR a partner tenant is actually chosen. "Partner" with no tenant
 *  is invalid (FB-03 — blocks create). */
export function reportingSatisfied(r: ReportingValue): boolean {
  return (
    r.choice === "our_team" || (r.tenantId !== null && r.tenantId.length > 0)
  );
}

/**
 * Create-mode reporting-subcontractor field (FB-03). Tenant-only by design —
 * the "address only / no sharing" (contact) option is intentionally NOT
 * offered here (the report can only be produced in our system, so a reporting
 * party must be a known tenant).
 */
export function ReportingPartyField({
  value,
  onChange,
  knownTenants,
  knownTenantsLoading,
  error,
}: {
  value: ReportingValue;
  onChange: (next: ReportingValue) => void;
  knownTenants: readonly KnownTenant[];
  knownTenantsLoading: boolean;
  error: string | null;
}): ReactElement {
  const options = useMemo(
    () =>
      [...knownTenants].sort((a, b) =>
        a.tenantName.localeCompare(b.tenantName),
      ),
    [knownTenants],
  );
  const noKnownTenants = !knownTenantsLoading && options.length === 0;

  return (
    <Box
      component="section"
      aria-label="Reporting subcontractor party"
      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
    >
      <PartyHeadline
        title="Reporting Subcontractor"
        error={error !== null}
        infoLabel="About the Reporting Subcontractor party"
        infoText={PARTY_INFO.reportingSubcontractor}
      />
      <Typography variant="caption" color="text.secondary">
        Who will do the reporting?
      </Typography>

      <RadioGroup
        value={value.choice}
        onChange={(_e, v) => {
          onChange({ choice: v as ReportingChoice, tenantId: null });
        }}
        aria-label="Who will do the reporting?"
      >
        <FormControlLabel
          value="our_team"
          control={<Radio size="small" />}
          label="Our team (we do the reporting)"
        />
        <FormControlLabel
          value="partner"
          control={<Radio size="small" />}
          label="A partner company"
        />
      </RadioGroup>

      {value.choice === "partner" ? (
        noKnownTenants ? (
          <Alert severity="warning" variant="outlined">
            You aren&apos;t linked to any partner companies yet. Invite them
            first, then you can hand the reporting to a partner.{" "}
            <Link
              component={RouterLink}
              to="/admin/partner-companies"
              underline="always"
            >
              Go to Partner Companies
            </Link>
          </Alert>
        ) : (
          <TextField
            select
            size="small"
            label="Reporting partner company"
            required
            error={error !== null}
            helperText={error ?? undefined}
            value={value.tenantId ?? ""}
            onChange={(e) => {
              const v = e.target.value;
              onChange({
                choice: "partner",
                tenantId: v.length > 0 ? v : null,
              });
            }}
            sx={{ maxWidth: 360 }}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {options.map((t) => (
              <MenuItem key={t.tenantId} value={t.tenantId}>
                {t.tenantName}
              </MenuItem>
            ))}
          </TextField>
        )
      ) : null}
    </Box>
  );
}

/* ── contract-scoped parties (FB-18) ─────────────────────────────────── */

/** FB-18 — `contract.contractor_tenant_id` / `contract.customer_tenant_id`.
 *  Per the role doc (`05_COMPANY_VISIBILITY.md`) these are contract-scoped
 *  parties, normally the SAME across all of a project's contracts (parks).
 *  Step 2 sets project-wide DEFAULTS; each park inherits them, overridable. */
export type ContractPartyKind = "contractContractor" | "contractCustomer";

const CONTRACT_PARTY_LABEL: Record<ContractPartyKind, string> = {
  contractContractor: "Contract contractor",
  contractCustomer: "Contract customer",
};

/* ── contract-scoped parties — tenant|contact toggle (FB-43) ─────────── */

/** FB-43 — a contract-scoped party value: either a partner tenant (auto-grants
 *  the contract-scoped role on the BE, PM-D24) or an address-book contact
 *  (descriptive only, never grants). Mirrors {@link PartyValue} but stands on
 *  its own type so the contract-party state (create draft + step-2 defaults +
 *  edit) reads clearly. At most one of `tenantId` / `contactId` is set; `mode`
 *  selects which the UI shows + which id rides along on the contract body. */
export interface ContractPartyValue {
  mode: PartyMode;
  tenantId: string | null;
  contactId: string | null;
}

export const EMPTY_CONTRACT_PARTY: ContractPartyValue = {
  mode: "tenant",
  tenantId: null,
  contactId: null,
};

/** FB-43 — resolve a `ContractPartyValue` from prefilled ids (an existing
 *  contract's `*_tenant_id` / `*_contact_id`). A contact id wins the mode (the
 *  contract was created "address only"); otherwise tenant mode (the default,
 *  even when both ids are null). Used to seed the edit pickers + the create
 *  step-2 defaults are seeded empty. */
export function contractPartyFromIds(
  tenantId: string | null | undefined,
  contactId: string | null | undefined,
): ContractPartyValue {
  if (contactId != null && contactId.length > 0) {
    return { mode: "contact", tenantId: null, contactId };
  }
  return {
    mode: "tenant",
    tenantId: tenantId != null && tenantId.length > 0 ? tenantId : null,
    contactId: null,
  };
}

/**
 * FB-43 — the contract-scoped party field with the SAME tenant|contact toggle
 * as the project `CreatePartyField`. Tenant mode → a prefillable known-tenant
 * select (auto-grants the contract role, tenant_admin-gated on the BE);
 * contact mode → the `ContactPicker` (descriptive only, never grants). Used on
 * the create step-2 defaults, the per-park `ParkCard`, and the edit per-contract
 * pickers. `prefilled` lets a migrated/existing tenant render its NAME even
 * when it isn't a current known link (mirrors `EditPartyField`).
 */
export function ContractPartyField({
  kind,
  label,
  value,
  onChange,
  knownTenants,
  knownTenantsLoading,
  prefilled = null,
}: {
  kind: ContractPartyKind;
  /** Override the field label (e.g. step-2 marks them "default for all parks"). */
  label?: string;
  value: ContractPartyValue;
  onChange: (next: ContractPartyValue) => void;
  knownTenants: readonly KnownTenant[];
  knownTenantsLoading: boolean;
  /** An existing party tenant + resolved name, so it shows even when it isn't a
   *  current known link (edit prefill). */
  prefilled?: PrefilledTenant | null;
}): ReactElement {
  const options = useMemo(
    () => optionsWithPrefilled(knownTenants, prefilled),
    [knownTenants, prefilled],
  );
  const noKnownTenants = !knownTenantsLoading && options.length === 0;
  const fieldLabel = label ?? CONTRACT_PARTY_LABEL[kind];

  return (
    <Box
      component="section"
      aria-label={`${CONTRACT_PARTY_LABEL[kind]} field`}
      sx={{ display: "flex", flexDirection: "column", gap: 1 }}
    >
      <RadioGroup
        row
        value={value.mode}
        onChange={(_e, v) => {
          onChange({ mode: v as PartyMode, tenantId: null, contactId: null });
        }}
        aria-label={`${CONTRACT_PARTY_LABEL[kind]} mode`}
      >
        <FormControlLabel
          value="tenant"
          control={<Radio size="small" />}
          label="Share with a partner company"
        />
        <FormControlLabel
          value="contact"
          control={<Radio size="small" />}
          label="Address only (no sharing)"
        />
      </RadioGroup>

      {value.mode === "contact" ? (
        <Box sx={{ minWidth: 240, maxWidth: 360, width: "100%" }}>
          <ContactPicker
            value={value.contactId}
            onChange={(contactId) => {
              onChange({ mode: "contact", tenantId: null, contactId });
            }}
            label={`${CONTRACT_PARTY_LABEL[kind]} contact`}
          />
        </Box>
      ) : noKnownTenants ? (
        <Alert severity="info" variant="outlined" sx={{ maxWidth: 360 }}>
          No partner companies to bind as the{" "}
          {CONTRACT_PARTY_LABEL[kind].toLowerCase()} yet.
        </Alert>
      ) : (
        <TextField
          select
          size="small"
          label={fieldLabel}
          value={value.tenantId ?? ""}
          onChange={(e) => {
            const v = e.target.value;
            onChange({
              mode: "tenant",
              tenantId: v.length > 0 ? v : null,
              contactId: null,
            });
          }}
          // FB-23 — fill the slot; capped so a flex row stays balanced.
          fullWidth
          sx={{ minWidth: 240, maxWidth: 360 }}
        >
          <MenuItem value="">
            <em>None</em>
          </MenuItem>
          {options.map((t) => (
            <MenuItem key={t.tenantId} value={t.tenantId}>
              {t.tenantName}
            </MenuItem>
          ))}
        </TextField>
      )}
    </Box>
  );
}

/* ── report language (FB-06) ─────────────────────────────────────────── */

/** The only languages the report pipeline supports today. The stored
 *  value is the ISO 639-1 code (`en` / `de`). Optional: empty ⇒ no language. */
export const REPORT_LANGUAGES: readonly { code: string; label: string }[] = [
  { code: "en", label: "English" },
  { code: "de", label: "German" },
];

/* ── staged parks + turbines ─────────────────────────────────────────── */

export interface DraftPark {
  /** Local-only stable key (NOT a server id — assigned on create). */
  key: string;
  siteName: string;
  /** ISO 3166-1 alpha-2 (assets/v1 stores alpha-2) — FB-05. */
  countryCode: string;
  isOffshore: boolean;
  /** FB-04 — set when the user picked an EXISTING site from the park
   *  typeahead. When present the park reuses this id and skips `POST /sites`;
   *  base fields are loaded from the site and locked (no override). */
  existingSiteId?: string | undefined;
  /** FB-18 / FB-43 — the park's `contract.contractor` party (tenant OR contact),
   *  prefilled from the step-2 project-wide default and overridable per park.
   *  Until the user explicitly edits it (`contractContractorOverridden`) the
   *  park tracks the step-2 default. */
  contractContractor?: ContractPartyValue | undefined;
  /** FB-18 / FB-43 — the park's `contract.customer` party (same default-tracking
   *  semantics as `contractContractor`). */
  contractCustomer?: ContractPartyValue | undefined;
  /** FB-18 — once the user explicitly sets the contract contractor on THIS
   *  park, it stops following the step-2 default. */
  contractContractorOverridden?: boolean | undefined;
  /** FB-18 — same, for the contract customer. */
  contractCustomerOverridden?: boolean | undefined;
}

export type OperatorMode = "none" | "tenant" | "contact";

export interface DraftTurbine {
  key: string;
  /** Local `DraftPark.key` this turbine attaches to. */
  parkKey: string;
  identifier: string;
  internalParkNumber: string;
  assetTypeId: string | null;
  assetModelId: string | null;
  operatorMode: OperatorMode;
  /** Set only when `operatorMode === 'tenant'`. Descriptive — never grants. */
  operatorTenantId: string | null;
  /** CM-9 — set only when `operatorMode === 'contact'`. The address-book
   *  contact chosen via the `ContactPicker`; maps to the turbine create body's
   *  `operatorContactId`. Descriptive only — never grants (PM-D22). */
  operatorContactId: string | null;
  /** FB-04 — set when the user picked an EXISTING turbine from the per-park
   *  typeahead. When present the row skips `POST /turbines`. */
  existingAssetId?: string | undefined;
  /** FB-16b — latitude captured from the EXISTING turbine the user picked
   *  (bulk-add or the per-row find-or-create). As of assets/v1@4.7.0 the list
   *  `Turbine` shape carries `latitude`/`longitude`, so existing-turbine drafts
   *  populate this and the create Review map can plot them. Stays undefined for
   *  new typed-in turbines (no location until a future coord-entry story). */
  latitude?: number | null | undefined;
  /** FB-16b — longitude captured from the EXISTING turbine the user picked. */
  longitude?: number | null | undefined;
}

/** Shape of a thrown `projectsFetch` error — the FE error envelope carries the
 *  HTTP status + the parsed JSON body (`{ code, reason }`) so callers can
 *  switch over a 409 reason. Mirrors `ProjectsFetchError` without importing the
 *  fetch module into the shared parts. */
export interface ProjectsFetchErrorLike {
  status?: number;
  body?: unknown;
}

let draftSeq = 0;
export function nextKey(prefix: string): string {
  draftSeq += 1;
  return `${prefix}-${String(draftSeq)}`;
}

/** FB-16b — read `latitude`/`longitude` off a turbine-search result. As of
 *  assets/v1@4.7.0 the list `Turbine` carries both (number | null), so this
 *  returns them for turbines with a recorded position and `{}` otherwise
 *  (null coords or a shape without them). Defensive (`unknown`) so any
 *  turbine-like object — list item or `TurbineDetail` — flows through. */
export function coordsFromTurbine(t: unknown): {
  latitude?: number;
  longitude?: number;
} {
  const rec = t as { latitude?: unknown; longitude?: unknown };
  const out: { latitude?: number; longitude?: number } = {};
  if (typeof rec.latitude === "number") out.latitude = rec.latitude;
  if (typeof rec.longitude === "number") out.longitude = rec.longitude;
  return out;
}

/** FB-17 — build a deselectable `DraftTurbine` from an EXISTING turbine of a
 *  park, with its base fields + identifier prefilled and `existingAssetId`
 *  set so the create flow skips `POST /turbines` and only links the position. */
export function draftFromExistingTurbine(
  parkKey: string,
  t: Turbine,
): DraftTurbine {
  return {
    key: nextKey("turbine"),
    parkKey,
    identifier: t.identifier ?? "",
    internalParkNumber: t.internalParkNumber ?? "",
    assetTypeId: t.assetTypeId,
    assetModelId: t.assetModelId,
    operatorMode: "none",
    operatorTenantId: null,
    operatorContactId: null,
    existingAssetId: t.id,
    ...coordsFromTurbine(t),
  };
}

/** Debounce a changing value (throttles the find-or-create typeaheads). */
export function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebounced(value);
    }, ms);
    return () => {
      window.clearTimeout(id);
    };
  }, [value, ms]);
  return debounced;
}

/* ── validation (FB-07 / FB-08 / FB-12) ──────────────────────────────── */

/** Per-park field errors, keyed by the draft `key`. */
export type ParkErrors = Record<string, { siteName?: string }>;
/** Per-turbine field errors, keyed by the draft `key`. */
export type TurbineErrors = Record<
  string,
  {
    parkKey?: string;
    identifier?: string;
    assetTypeId?: string;
    assetModelId?: string;
  }
>;

/** Everything the holistic validator can flag in one pass (FB-08). */
export interface WizardErrors {
  projectName?: string;
  /** M3C-04 — a Basics field the version marks `required`, left empty. */
  fields?: Partial<Record<BasicsField, string>>;
  customer?: string;
  contractor?: string;
  /** FB-03 — "A partner company" chosen but no reporting tenant selected. */
  reporting?: string;
  parks: ParkErrors;
  turbines: TurbineErrors;
}

export const STEP_LABELS = [
  "Project basics",
  "Parties",
  "Parks & Turbines",
  "Review",
];

/** The edit-mode stepper relabels the last step "Overview" (PM-D: the Overview
 *  tab IS the Review step). Same four-step shape; only the copy differs. */
export const EDIT_STEP_LABELS = [
  "Project basics",
  "Parties",
  "Parks & Turbines",
  "Overview",
];

/** Validate the ENTIRE wizard at once (FB-08) — never stop at the first
 *  error. Returns the flagged-field map; emptiness ⇒ valid. */
export function validateWizard(input: {
  trimmedName: string;
  customer: PartyValue;
  contractor: PartyValue;
  reporting: ReportingValue;
  parks: readonly DraftPark[];
  turbines: readonly DraftTurbine[];
  /** M3C-04 — the picked version's field rules (absent = every field as today). */
  rules?: FieldRules;
  /** M3C-04 — the Basics values a `required` rule checks. */
  basics?: Partial<Record<BasicsField, string | readonly string[]>>;
}): WizardErrors {
  const errors: WizardErrors = { parks: {}, turbines: {} };
  const rules = input.rules ?? TODAY_RULES;

  if (input.trimmedName.length === 0) {
    errors.projectName = "Project name is required.";
  }

  // M3C-04 — a Basics field the version marks `required` must be filled; a
  // hidden or prefilled one is never validated.
  for (const f of BASICS_FIELDS) {
    if (!isRequired(rules[f])) continue;
    const v = input.basics?.[f];
    const empty =
      v === undefined ||
      (typeof v === "string" ? v.trim().length === 0 : v.length === 0);
    if (empty) {
      errors.fields = {
        ...(errors.fields ?? {}),
        [f]: `${BASICS_LABELS[f]} is required by the workflow.`,
      };
    }
  }

  // CM-9 — a party is satisfied by EITHER a chosen partner tenant (auto-grants)
  // OR, in "Address only (contact)" mode, a chosen address-book contact
  // (descriptive only, never grants — CM-D13). M3C-04 — only a party the
  // version leaves visible, not prefilled and required is validated.
  const partySatisfied = (p: PartyValue): boolean =>
    p.mode === "tenant"
      ? p.tenantId !== null && p.tenantId.length > 0
      : p.contactId !== null && p.contactId.length > 0;
  const customerRules = partyRules(rules, "customer");
  const contractorRules = partyRules(rules, "contractor");
  if (
    customerRules.required &&
    !customerRules.hidden &&
    !partySatisfied(input.customer)
  ) {
    errors.customer = "Choose the customer partner company.";
  }
  if (
    contractorRules.required &&
    !contractorRules.hidden &&
    !partySatisfied(input.contractor)
  ) {
    errors.contractor = "Choose the contractor partner company.";
  }
  const reportingRule = rules.reportingSubcontractorTenantId;
  if (!isHidden(reportingRule) && !isPrefilled(reportingRule)) {
    const needsPartner =
      isRequired(reportingRule) &&
      (input.reporting.tenantId === null ||
        input.reporting.tenantId.length === 0);
    if (needsPartner || !reportingSatisfied(input.reporting)) {
      errors.reporting = "Choose the reporting partner company.";
    }
  }

  for (const park of input.parks) {
    if (
      park.existingSiteId === undefined &&
      park.siteName.trim().length === 0
    ) {
      errors.parks[park.key] = { siteName: "Park name is required." };
    }
  }

  for (const turbine of input.turbines) {
    const t: TurbineErrors[string] = {};
    const park = input.parks.find((p) => p.key === turbine.parkKey);
    if (park === undefined) {
      t.parkKey = "Attach this turbine to a park.";
    }
    if (turbine.existingAssetId === undefined) {
      if (turbine.identifier.trim().length === 0) {
        t.identifier = "Identifier is required.";
      }
      if (turbine.assetTypeId === null) {
        t.assetTypeId = "Asset type is required.";
      }
      if (turbine.assetModelId === null) {
        t.assetModelId = "Model is required.";
      }
    }
    if (Object.keys(t).length > 0) {
      errors.turbines[turbine.key] = t;
    }
  }

  return errors;
}

/** Which step indexes carry at least one error (drives the Stepper flags). */
export function stepsWithErrors(errors: WizardErrors): Set<number> {
  const flagged = new Set<number>();
  if (errors.projectName !== undefined) flagged.add(0);
  if (errors.fields && Object.keys(errors.fields).length > 0) flagged.add(0);
  if (
    errors.customer !== undefined ||
    errors.contractor !== undefined ||
    errors.reporting !== undefined
  ) {
    flagged.add(1);
  }
  if (
    Object.keys(errors.parks).length > 0 ||
    Object.keys(errors.turbines).length > 0
  ) {
    flagged.add(2);
  }
  return flagged;
}

export function hasAnyError(errors: WizardErrors): boolean {
  return (
    errors.projectName !== undefined ||
    (errors.fields !== undefined && Object.keys(errors.fields).length > 0) ||
    errors.customer !== undefined ||
    errors.contractor !== undefined ||
    errors.reporting !== undefined ||
    Object.keys(errors.parks).length > 0 ||
    Object.keys(errors.turbines).length > 0
  );
}

/* ── one park row editor (FB-04 / FB-05) ─────────────────────────────── */

/** Free-solo option for the park typeahead: either an existing `Site`
 *  loaded from the server, or the raw string the user typed (new park). */
type ParkOption = Site | string;

export function ParkRow({
  park,
  error,
  onChange,
  onRemove,
}: {
  park: DraftPark;
  error: ParkErrors[string] | undefined;
  onChange: (patch: Partial<DraftPark>) => void;
  onRemove: () => void;
}): ReactElement {
  const isExisting = park.existingSiteId !== undefined;

  const [parkInput, setParkInput] = useState(park.siteName);
  const debouncedParkInput = useDebounced(parkInput, 300);
  const { data: searchData, isFetching: searchLoading } = useSitesSearchQuery(
    isExisting ? "" : debouncedParkInput,
  );
  const options: readonly Site[] = useMemo(
    () => searchData?.items ?? [],
    [searchData?.items],
  );

  const value: ParkOption | null = isExisting
    ? (options.find((s) => s.id === park.existingSiteId) ?? {
        id: park.existingSiteId ?? "",
        siteCode: 0,
        siteName: park.siteName,
        countryCode: park.countryCode.length > 0 ? park.countryCode : null,
        isOffshore: park.isOffshore,
        assetCount: 0,
        createdAt: "",
        createdBy: null,
        deletedAt: null,
        isActive: true,
      })
    : park.siteName.length > 0
      ? park.siteName
      : null;

  function pickExisting(site: Site): void {
    onChange({
      existingSiteId: site.id,
      siteName: site.siteName,
      countryCode: site.countryCode ?? "",
      isOffshore: site.isOffshore,
    });
  }

  function revertToNew(): void {
    onChange({ existingSiteId: undefined });
    setParkInput(park.siteName);
  }

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ sm: "flex-start" }}
        useFlexGap
        // FB-24 — name (min 220) + country (220) + offshore + remove can't all
        // fit a constrained track and, without wrap, push a horizontal
        // scrollbar on the wizard scroll container. `flexWrap` lets the trailing
        // controls drop to a new line at narrow widths instead of overflowing.
        sx={{ flexWrap: { sm: "wrap" } }}
      >
        <Box sx={{ flex: "1 1 220px", minWidth: 220 }}>
          <Autocomplete<ParkOption, false, false, true>
            freeSolo
            options={options}
            value={value}
            loading={searchLoading}
            readOnly={isExisting}
            filterOptions={(opts) => opts}
            getOptionLabel={(opt) =>
              typeof opt === "string" ? opt : opt.siteName
            }
            isOptionEqualToValue={(o, v) =>
              typeof o !== "string" && typeof v !== "string" && o.id === v.id
            }
            onInputChange={(_e, next, reason) => {
              if (isExisting) return;
              if (reason === "input" || reason === "clear") {
                setParkInput(next);
                onChange({ siteName: next });
              }
            }}
            onChange={(_e, next) => {
              if (next === null) {
                onChange({ siteName: "" });
                setParkInput("");
                return;
              }
              if (typeof next === "string") {
                onChange({ siteName: next });
                setParkInput(next);
                return;
              }
              pickExisting(next);
            }}
            renderOption={(props, opt) =>
              typeof opt === "string" ? (
                <li {...props}>{opt}</li>
              ) : (
                <li {...props} key={opt.id}>
                  <Box component="span">{opt.siteName}</Box>
                  {opt.countryCode ? (
                    <Box
                      component="span"
                      sx={{ ml: 1, color: "text.secondary" }}
                    >
                      — {opt.countryCode}
                    </Box>
                  ) : null}
                </li>
              )
            }
            renderInput={(params) => {
              /* eslint-disable @typescript-eslint/no-explicit-any */
              const passThrough: any = params;
              return (
                <TextField
                  {...passThrough}
                  size="small"
                  label="Park name"
                  required
                  error={error?.siteName !== undefined}
                  helperText={
                    error?.siteName ??
                    (isExisting
                      ? "Loaded from the catalogue — base details are locked."
                      : "Type to search existing parks, or enter a new name.")
                  }
                  slotProps={{
                    htmlInput: {
                      ...params.inputProps,
                      "aria-label": "Park name",
                      ...(isExisting ? { readOnly: true } : {}),
                    },
                  }}
                />
              );
              /* eslint-enable */
            }}
          />
          {isExisting ? (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ mt: 0.5 }}
            >
              <Chip size="small" color="info" label="Existing park" />
              <Button size="small" variant="text" onClick={revertToNew}>
                Use a new park instead
              </Button>
            </Stack>
          ) : null}
        </Box>

        <Box sx={{ width: 220 }}>
          <CountryAutocomplete
            value={park.countryCode.length > 0 ? park.countryCode : null}
            onChange={(next) => {
              onChange({ countryCode: next ?? "" });
            }}
            readOnly={isExisting}
          />
        </Box>

        <FormControlLabel
          sx={{ mt: 0.5 }}
          control={
            <Checkbox
              size="small"
              checked={park.isOffshore}
              disabled={isExisting}
              onChange={(e) => {
                onChange({ isOffshore: e.target.checked });
              }}
            />
          }
          label="Offshore"
        />
        <Tooltip title="Remove park">
          <IconButton
            size="small"
            aria-label="Remove park"
            onClick={onRemove}
            sx={{ mt: 0.5 }}
          >
            <Trash2Icon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    </Paper>
  );
}

/* ── one turbine row editor ──────────────────────────────────────────── */

export function TurbineRow({
  turbine,
  parks,
  knownTenants,
  errors,
  onChange,
  onRemove,
  hidePark = false,
}: {
  turbine: DraftTurbine;
  parks: readonly DraftPark[];
  knownTenants: readonly KnownTenant[];
  errors: TurbineErrors[string] | undefined;
  onChange: (next: DraftTurbine) => void;
  onRemove: () => void;
  /** FB-17 — hide the per-row Park selector when the row already lives under
   *  a single park card (the park-centric create step). The park is fixed by
   *  the card; showing a selector would be redundant. */
  hidePark?: boolean;
}): ReactElement {
  const [modelDialogOpen, setModelDialogOpen] = useState(false);

  const allModelsQuery = useAssetModelsForTypeQuery(null);
  const cascadingModelsQuery = useAssetModelsForTypeQuery(turbine.assetTypeId);
  const assetTypeOptions = allModelsQuery.data?.assetTypes ?? [];
  const modelOptions = useMemo(
    () => cascadingModelsQuery.data?.items ?? [],
    [cascadingModelsQuery.data?.items],
  );

  const selectedPark = parks.find((p) => p.key === turbine.parkKey);
  const parkSiteId = selectedPark?.existingSiteId;
  const isExistingTurbine = turbine.existingAssetId !== undefined;

  const [identifierInput, setIdentifierInput] = useState(turbine.identifier);
  const debouncedIdentifier = useDebounced(identifierInput, 300);
  const { data: turbineSearchData, isFetching: turbineSearchLoading } =
    useTurbinesSearchQuery(
      isExistingTurbine ? null : (parkSiteId ?? null),
      debouncedIdentifier,
    );
  const turbineOptions: readonly Turbine[] = useMemo(
    () => turbineSearchData?.items ?? [],
    [turbineSearchData?.items],
  );
  const tenantOptions = useMemo(
    () =>
      [...knownTenants].sort((a, b) =>
        a.tenantName.localeCompare(b.tenantName),
      ),
    [knownTenants],
  );

  const parkLabel = (p: DraftPark): string =>
    p.siteName.trim().length > 0 ? p.siteName.trim() : "(unnamed park)";

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="subtitle2">Turbine</Typography>
            {isExistingTurbine ? (
              <>
                <Chip size="small" color="info" label="Existing turbine" />
                <Button
                  size="small"
                  variant="text"
                  onClick={() => {
                    onChange({ ...turbine, existingAssetId: undefined });
                    setIdentifierInput(turbine.identifier);
                  }}
                >
                  Use a new turbine instead
                </Button>
              </>
            ) : null}
          </Stack>
          <Tooltip title="Remove turbine">
            <IconButton
              size="small"
              aria-label="Remove turbine"
              onClick={onRemove}
            >
              <Trash2Icon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          useFlexGap
          // FB-24 — wrap the min-200 park/identifier pair on narrow tracks.
          sx={{ flexWrap: { sm: "wrap" } }}
        >
          {hidePark ? null : (
            <TextField
              select
              size="small"
              label="Park"
              required
              error={errors?.parkKey !== undefined}
              helperText={errors?.parkKey}
              value={turbine.parkKey}
              onChange={(e) => {
                onChange({ ...turbine, parkKey: e.target.value });
              }}
              sx={{ minWidth: 200, flex: 1 }}
              slotProps={{ htmlInput: { "aria-label": "Park" } }}
            >
              {parks.length === 0 ? (
                <MenuItem value="" disabled>
                  Add a park first
                </MenuItem>
              ) : (
                parks.map((p) => (
                  <MenuItem key={p.key} value={p.key}>
                    {parkLabel(p)}
                  </MenuItem>
                ))
              )}
            </TextField>
          )}

          {parkSiteId !== undefined ? (
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Autocomplete<Turbine, false, false, true>
                freeSolo
                options={turbineOptions}
                loading={turbineSearchLoading}
                readOnly={isExistingTurbine}
                filterOptions={(opts) => opts}
                value={
                  isExistingTurbine
                    ? (turbineOptions.find(
                        (t) => t.id === turbine.existingAssetId,
                      ) ?? turbine.identifier)
                    : turbine.identifier.length > 0
                      ? turbine.identifier
                      : null
                }
                getOptionLabel={(opt) =>
                  typeof opt === "string" ? opt : (opt.identifier ?? "")
                }
                isOptionEqualToValue={(o, v) =>
                  typeof o !== "string" &&
                  typeof v !== "string" &&
                  o.id === v.id
                }
                onInputChange={(_e, next, reason) => {
                  if (isExistingTurbine) return;
                  if (reason === "input" || reason === "clear") {
                    setIdentifierInput(next);
                    onChange({ ...turbine, identifier: next });
                  }
                }}
                onChange={(_e, next) => {
                  if (next === null) {
                    onChange({ ...turbine, identifier: "" });
                    setIdentifierInput("");
                    return;
                  }
                  if (typeof next === "string") {
                    onChange({ ...turbine, identifier: next });
                    setIdentifierInput(next);
                    return;
                  }
                  onChange({
                    ...turbine,
                    existingAssetId: next.id,
                    identifier: next.identifier ?? "",
                    internalParkNumber: next.internalParkNumber ?? "",
                    assetTypeId: next.assetTypeId,
                    assetModelId: next.assetModelId,
                    // FB-16b — capture the picked turbine's coords (assets/v1
                    // @4.7.0 list item) so the create Review map can plot it.
                    ...coordsFromTurbine(next),
                  });
                  setIdentifierInput(next.identifier ?? "");
                }}
                renderOption={(props, opt) =>
                  typeof opt === "string" ? (
                    <li {...props}>{opt}</li>
                  ) : (
                    <li {...props} key={opt.id}>
                      <Box component="span">
                        {opt.identifier ?? `#${String(opt.assetCode)}`}
                      </Box>
                      <Box
                        component="span"
                        sx={{ ml: 1, color: "text.secondary" }}
                      >
                        — {opt.modelName}
                      </Box>
                    </li>
                  )
                }
                renderInput={(params) => {
                  /* eslint-disable @typescript-eslint/no-explicit-any */
                  const passThrough: any = params;
                  return (
                    <TextField
                      {...passThrough}
                      size="small"
                      label="Identifier"
                      required
                      error={errors?.identifier !== undefined}
                      helperText={
                        errors?.identifier ??
                        (isExistingTurbine
                          ? "Loaded from the catalogue — locked."
                          : "Type to search existing turbines, or enter a new one.")
                      }
                      slotProps={{
                        htmlInput: {
                          ...params.inputProps,
                          "aria-label": "Identifier",
                          ...(isExistingTurbine ? { readOnly: true } : {}),
                        },
                      }}
                    />
                  );
                  /* eslint-enable */
                }}
              />
            </Box>
          ) : (
            <TextField
              size="small"
              label="Identifier"
              required
              placeholder="e.g. 85586 / E781493"
              error={errors?.identifier !== undefined}
              helperText={errors?.identifier}
              value={turbine.identifier}
              onChange={(e) => {
                onChange({ ...turbine, identifier: e.target.value });
              }}
              sx={{ flex: 1 }}
            />
          )}
          <TextField
            size="small"
            label="Internal park number"
            placeholder="e.g. WEA01"
            disabled={isExistingTurbine}
            value={turbine.internalParkNumber}
            onChange={(e) => {
              onChange({ ...turbine, internalParkNumber: e.target.value });
            }}
            sx={{ flex: 1 }}
          />
        </Stack>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems="flex-start"
          useFlexGap
          // FB-24 — type + model + "New model" can't fit a constrained track;
          // wrap so the button drops below instead of pushing a scrollbar.
          sx={{ flexWrap: { sm: "wrap" } }}
        >
          <Box sx={{ flex: "1 1 200px", minWidth: 200 }}>
            <AssetTypeAutocomplete
              value={turbine.assetTypeId}
              options={assetTypeOptions}
              loading={allModelsQuery.isLoading}
              required
              disabled={isExistingTurbine}
              errorText={errors?.assetTypeId ?? null}
              onChange={(id) => {
                onChange({
                  ...turbine,
                  assetTypeId: id,
                  assetModelId: null,
                });
              }}
            />
          </Box>
          <Box sx={{ flex: "1 1 200px", minWidth: 200 }}>
            <AssetModelAutocomplete
              value={turbine.assetModelId}
              options={modelOptions}
              loading={cascadingModelsQuery.isFetching}
              disabled={isExistingTurbine || turbine.assetTypeId === null}
              required
              errorText={errors?.assetModelId ?? null}
              label="Model"
              onChange={(id) => {
                onChange({ ...turbine, assetModelId: id });
              }}
            />
          </Box>
          <Button
            size="small"
            startIcon={<PlusIcon fontSize="small" />}
            disabled={isExistingTurbine}
            onClick={() => {
              setModelDialogOpen(true);
            }}
            sx={{ mt: 1, whiteSpace: "nowrap" }}
          >
            New model
          </Button>
        </Stack>

        <Box>
          <Typography variant="caption" color="text.secondary">
            Operator (the company that runs this turbine — descriptive only,
            never shares the project)
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="flex-start"
            useFlexGap
            // FB-24 — radio group + the min-240 operator select wrap on narrow
            // tracks instead of overflowing.
            sx={{ flexWrap: { sm: "wrap" } }}
          >
            <RadioGroup
              row
              value={turbine.operatorMode}
              onChange={(_e, v) => {
                onChange({
                  ...turbine,
                  operatorMode: v as OperatorMode,
                  operatorTenantId: null,
                  operatorContactId: null,
                });
              }}
              aria-label="Operator mode"
            >
              <FormControlLabel
                value="none"
                control={<Radio size="small" />}
                label="None"
              />
              <FormControlLabel
                value="tenant"
                control={<Radio size="small" />}
                label="Partner company"
              />
              <FormControlLabel
                value="contact"
                control={<Radio size="small" />}
                label="Contact only"
              />
            </RadioGroup>
            {turbine.operatorMode === "tenant" ? (
              <TextField
                select
                size="small"
                label="Operator company"
                aria-label="Operator company"
                value={turbine.operatorTenantId ?? ""}
                onChange={(e) => {
                  const v = e.target.value;
                  onChange({
                    ...turbine,
                    operatorTenantId: v.length > 0 ? v : null,
                  });
                }}
                sx={{ minWidth: 240 }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {tenantOptions.map((t) => (
                  <MenuItem key={t.tenantId} value={t.tenantId}>
                    {t.tenantName}
                  </MenuItem>
                ))}
              </TextField>
            ) : null}
            {turbine.operatorMode === "contact" ? (
              /* CM-9 — the real ContactPicker for the operator. The chosen
                 contactId maps to the turbine create body's `operatorContactId`;
                 descriptive only, never grants (PM-D22 / CM-D13). */
              <Box sx={{ flex: 1, minWidth: 240 }}>
                <ContactPicker
                  value={turbine.operatorContactId}
                  onChange={(contactId) => {
                    onChange({ ...turbine, operatorContactId: contactId });
                  }}
                  label="Operator contact"
                />
              </Box>
            ) : null}
          </Stack>
        </Box>
      </Stack>

      <AddTurbineModelDialog
        open={modelDialogOpen}
        onClose={() => {
          setModelDialogOpen(false);
        }}
        assetTypes={assetTypeOptions}
      />
    </Paper>
  );
}

/* ── park-centric card (FB-17 / FB-18, create-mode) ──────────────────── */

/**
 * FB-17 + FB-18 — the create-step park card. Replaces the old flat
 * parks-list + turbines-list with a park-CENTRIC layout:
 *
 *  - the park find-or-create header (`ParkRow`);
 *  - FB-18 — the contract-scoped contractor/customer pickers (tenant_admin
 *    only), prefilled from the step-2 defaults, overridable per park;
 *  - FB-17 — when the park resolves to an EXISTING site, ALL its turbines are
 *    auto-added (≈ 1 click) as deselectable chips ("Added all N turbines …"),
 *    each removable with an × . Idempotent on re-resolve (never duplicates an
 *    already-added asset);
 *  - the "+ Add a new turbine" rows (the find-or-create non-existing path),
 *    available for both existing and new parks.
 *
 * Turbine state is owned by the parent (the wizard's lifted draft list); this
 * card receives only THIS park's turbines + the add/update/remove/bulk-add
 * callbacks, so a single source of truth still drives submit + validation.
 */
export function ParkCard({
  park,
  parkError,
  turbines,
  turbineErrors,
  knownTenants,
  knownTenantsLoading,
  contractContractorDefault,
  contractCustomerDefault,
  showErrors,
  onChangePark,
  onRemovePark,
  onAddTurbine,
  onUpdateTurbine,
  onRemoveTurbine,
  onBulkSetExisting,
}: {
  park: DraftPark;
  parkError: ParkErrors[string] | undefined;
  /** This park's turbines (the parent filters by `parkKey`). */
  turbines: readonly DraftTurbine[];
  turbineErrors: TurbineErrors;
  knownTenants: readonly KnownTenant[];
  knownTenantsLoading: boolean;
  contractContractorDefault: ContractPartyValue;
  contractCustomerDefault: ContractPartyValue;
  showErrors: boolean;
  onChangePark: (patch: Partial<DraftPark>) => void;
  onRemovePark: () => void;
  onAddTurbine: () => void;
  onUpdateTurbine: (next: DraftTurbine) => void;
  onRemoveTurbine: (key: string) => void;
  /** FB-17 — replace this park's EXISTING (bulk-added) turbines with the given
   *  set, computed by the parent so the lifted list stays the source of truth.
   *  Idempotent: passing the same assets a second time is a no-op. */
  onBulkSetExisting: (drafts: readonly DraftTurbine[]) => void;
}): ReactElement {
  const isExisting = park.existingSiteId !== undefined;

  // FB-17 — fetch the existing park's turbines for the one-click bulk add.
  const siteTurbinesQuery = useSiteTurbinesQuery(
    isExisting ? park.existingSiteId : null,
    isExisting,
  );
  const siteTurbines = useMemo(
    () => siteTurbinesQuery.data?.items ?? [],
    [siteTurbinesQuery.data?.items],
  );

  // The set of existing-asset ids already staged under this park (so the
  // bulk-add is idempotent + the chips reflect what's added).
  const existingTurbines = useMemo(
    () => turbines.filter((t) => t.existingAssetId !== undefined),
    [turbines],
  );
  const newTurbines = useMemo(
    () => turbines.filter((t) => t.existingAssetId === undefined),
    [turbines],
  );
  const stagedAssetIds = useMemo(
    () =>
      new Set(
        existingTurbines
          .map((t) => t.existingAssetId)
          .filter((id): id is string => id !== undefined),
      ),
    [existingTurbines],
  );

  // FB-17 — auto-add ALL the park's turbines on resolve. Fires once the
  // site-turbines query settles for an existing park; idempotent because it
  // only ADDS assets not already staged (re-resolve / refetch won't dupe, and
  // a user-removed turbine stays removed within the same resolved set unless
  // the park is re-resolved to a different site).
  const autoAddedSiteRef = useState<string | null>(null);
  const [autoAddedSite, setAutoAddedSite] = autoAddedSiteRef;
  useEffect(() => {
    if (!isExisting || park.existingSiteId === undefined) {
      if (autoAddedSite !== null) setAutoAddedSite(null);
      // Reverting to a new park drops the previously bulk-added existing
      // turbines (a new park has no catalogue turbines), keeping any
      // manually-added rows.
      if (existingTurbines.length > 0) onBulkSetExisting([]);
      return;
    }
    if (siteTurbinesQuery.isFetching) return;
    if (autoAddedSite === park.existingSiteId) return;
    // First settle for this site → stage every turbine not already present.
    const additions = siteTurbines
      .filter((t) => !stagedAssetIds.has(t.id))
      .map((t) => draftFromExistingTurbine(park.key, t));
    if (additions.length > 0) {
      onBulkSetExisting([...existingTurbines, ...additions]);
    }
    setAutoAddedSite(park.existingSiteId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isExisting,
    park.existingSiteId,
    park.key,
    siteTurbinesQuery.isFetching,
    siteTurbines,
  ]);

  const bulkCount = existingTurbines.length;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        {/* Park find-or-create header. */}
        <ParkRow
          park={park}
          error={showErrors ? parkError : undefined}
          onChange={onChangePark}
          onRemove={onRemovePark}
        />

        {/* FB-18/FB-44 — contract-scoped parties, prefilled from the step-2
            defaults and overridable per park. Rendered for everyone who can
            edit the project (like the project parties); the BE 403s a non-admin
            tenant-bind (PM-D23). */}
        <Box
          component="section"
          aria-label="Contract parties"
          sx={{ display: "flex", flexDirection: "column", gap: 1 }}
        >
          <Typography variant="subtitle2">Contract parties</Typography>
          <Typography variant="caption" color="text.secondary">
            Defaults to the project-wide contract parties — override here for
            this park if needed.
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            alignItems="flex-start"
            useFlexGap
            // FB-24 — the two min-240 contract-party pickers wrap on narrow
            // tracks instead of overflowing the wizard scroll container.
            sx={{ flexWrap: { sm: "wrap" } }}
          >
            <ContractPartyField
              kind="contractContractor"
              value={
                park.contractContractorOverridden === true
                  ? (park.contractContractor ?? EMPTY_CONTRACT_PARTY)
                  : contractContractorDefault
              }
              onChange={(next) => {
                onChangePark({
                  contractContractor: next,
                  contractContractorOverridden: true,
                });
              }}
              knownTenants={knownTenants}
              knownTenantsLoading={knownTenantsLoading}
            />
            <ContractPartyField
              kind="contractCustomer"
              value={
                park.contractCustomerOverridden === true
                  ? (park.contractCustomer ?? EMPTY_CONTRACT_PARTY)
                  : contractCustomerDefault
              }
              onChange={(next) => {
                onChangePark({
                  contractCustomer: next,
                  contractCustomerOverridden: true,
                });
              }}
              knownTenants={knownTenants}
              knownTenantsLoading={knownTenantsLoading}
            />
          </Stack>
        </Box>

        <Divider />

        {/* FB-17 — bulk-added existing turbines (deselectable chips). */}
        {isExisting ? (
          <Box
            component="section"
            aria-label="Existing turbines from this park"
          >
            {siteTurbinesQuery.isFetching && bulkCount === 0 ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="caption" color="text.secondary">
                  Loading this park&apos;s turbines…
                </Typography>
              </Stack>
            ) : bulkCount > 0 ? (
              <>
                <Typography variant="caption" color="text.secondary">
                  Added all {bulkCount}{" "}
                  {bulkCount === 1 ? "turbine" : "turbines"} from this park.
                  Remove any you don&apos;t want.
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 1,
                    mt: 1,
                  }}
                >
                  {existingTurbines.map((t) => (
                    <Chip
                      key={t.key}
                      size="small"
                      color="info"
                      variant="outlined"
                      label={
                        t.identifier.length > 0
                          ? t.identifier
                          : "(unnamed turbine)"
                      }
                      onDelete={() => {
                        onRemoveTurbine(t.key);
                      }}
                    />
                  ))}
                </Box>
              </>
            ) : (
              <Typography variant="caption" color="text.secondary">
                This park has no turbines yet — add one below.
              </Typography>
            )}
          </Box>
        ) : null}

        {/* "+ Add a new turbine" (the find-or-create non-existing path). */}
        <Box component="section" aria-label="New turbines">
          {newTurbines.length > 0 ? (
            <Stack spacing={2} sx={{ mb: 1 }}>
              {newTurbines.map((turbine) => (
                <TurbineRow
                  key={turbine.key}
                  turbine={turbine}
                  parks={[park]}
                  knownTenants={knownTenants}
                  errors={showErrors ? turbineErrors[turbine.key] : undefined}
                  onChange={onUpdateTurbine}
                  onRemove={() => {
                    onRemoveTurbine(turbine.key);
                  }}
                  hidePark
                />
              ))}
            </Stack>
          ) : null}
          <Button
            size="small"
            startIcon={<PlusIcon fontSize="small" />}
            onClick={onAddTurbine}
          >
            Add a new turbine
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
}
